import { Observable } from 'rxjs';

import { Injectable } from '@angular/core';

import { SnAlarm, SnCamera, SnFloor, SnLocation, SnTags } from '@lib/api/entities';
import { FsMapLocationsServiceProvider, IFsMap } from '@lib/geo/core';
import { LocationsService } from '@lib/locations/services/locations.service';

@Injectable()
export class MapLocationsFacadeService<
  TMap extends IFsMap,
> extends FsMapLocationsServiceProvider<TMap> {
  constructor(
    private readonly provider: FsMapLocationsServiceProvider<TMap>,
    private readonly locationsService: LocationsService,
  ) {
    super();
  }

  public get geospatialProvider(): string {
    return this.provider.geospatialProvider;
  }

  public get id(): string {
    return this.provider.id;
  }

  public get cameraSelected$(): Observable<SnCamera> {
    return this.locationsService.cameraSelected$;
  }

  public get cameraTags$(): Observable<SnTags> {
    return this.locationsService.cameraTags$;
  }

  public get removePolyline$(): Observable<void> {
    return this.locationsService.removePolyline$;
  }

  public get hovered$(): Observable<SnCamera | null> {
    return this.locationsService.hovered$;
  }

  public get sdsFloorCameras$(): Observable<SnCamera[]> {
    return this.locationsService.sdsFloorCameras$;
  }

  public get selectedFloor$(): Observable<SnFloor | null> {
    return this.locationsService.selectedFloor$;
  }

  public get selected(): SnLocation {
    return this.locationsService.selected;
  }

  public get selectedFloor(): SnFloor {
    return this.locationsService.selectedFloor;
  }

  public get isRoomClearingEnabled(): boolean {
    return this.locationsService.isRoomClearingEnabled;
  }

  public get selectedNumCameras(): number {
    return this.locationsService.selectedNumCameras;
  }

  public get roomFullyCleared(): boolean {
    return this.locationsService.roomFullyCleared;
  }

  public get selectedFloorHasCameras(): boolean {
    return this.locationsService.selectedFloorHasCameras;
  }

  public get selectionChange$(): Observable<SnLocation | null> {
    return this.locationsService.selectionChange$;
  }

  public getMarkers(): any[] {
    return this.provider.getMarkers();
  }

  public onMapLoaded(map: TMap): void {
    this.provider.onMapLoaded(map);
  }

  public isSelected(id: string): boolean {
    return this.locationsService.isSelected(id);
  }

  public select(id: string, data?: any, move?: boolean, keepAlarmPopup?: boolean): void {
    this.provider.setSelectionParams(id, move ?? true, keepAlarmPopup ?? false);
    this.locationsService.selectLocation(id, data, move, keepAlarmPopup).subscribe();
  }

  public deselectAlarm(): void {
    this.provider.deselectAlarm();
  }

  public deselect(): void {
    this.locationsService.deselectLocation();
  }

  public setSelectionParams(locationId: string, move: boolean, keepAlarmPopup: boolean): void {
    this.provider.setSelectionParams(locationId, move, keepAlarmPopup);
  }

  public addToMap(): void {
    this.provider.addToMap();
  }

  public removeFromMap(): void {
    this.provider.removeFromMap();
  }

  public setFloor(floor: SnFloor): void {
    this.locationsService.setSelectedFloor(floor);
  }

  public clearAllRooms(): void {
    this.locationsService.clearAllRooms().subscribe();
  }

  public removeClearAllRooms(): void {
    this.locationsService.removeClearAllRooms().subscribe();
  }

  public clearRoom(release: any): void {
    this.provider.clearRoom(release);
  }

  public showRoomClearedIcons(openRoomClearingMenu: boolean): void {
    this.provider.showRoomClearedIcons(openRoomClearingMenu);
  }

  public removeAllClearRoomMarkers(): void {
    this.provider.removeAllClearRoomMarkers();
  }

  public getCameras(): SnCamera[] {
    return this.provider.getCameras();
  }

  public getNearestLocationForSDS(alarm: SnAlarm): void {
    this.locationsService.getNearestLocationForSDS(alarm).subscribe((location) => {
      if (location) {
        this.select(location.id, alarm);
      }
    });
  }
}
